export function toISODate(d) {
  const y = d.getFullYear(), m = d.getMonth() + 1, day = d.getDate();
  return `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function parseISODate(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

// Given a preset and an end date (YYYY-MM-DD), return start date so the range fits the preset.
export function startDateForPreset(dateRange, endDateStr) {
  const end = parseISODate(endDateStr);
  end.setHours(0, 0, 0, 0);
  let start;
  if (dateRange === "This Week") {
    const day = end.getDay();
    start = new Date(end);
    start.setDate(end.getDate() - day);
  } else if (dateRange === "MTD") {
    start = new Date(end.getFullYear(), end.getMonth(), 1);
  } else if (dateRange === "Last Week") {
    const now = new Date();
    start = new Date(now);
    start.setDate(now.getDate() - now.getDay() - 7);
  } else if (dateRange === "Last Month") {
    const now = new Date();
    start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  } else if (dateRange === "Last Quarter") {
    const now = new Date();
    const prevQ = (Math.floor(now.getMonth() / 3) + 3) % 4;
    const year = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
    start = new Date(year, prevQ * 3, 1);
  } else if (dateRange === "Q1") {
    start = new Date(new Date().getFullYear(), 0, 1);
  } else if (dateRange === "Q2") {
    start = new Date(new Date().getFullYear(), 3, 1);
  } else if (dateRange === "Q3") {
    start = new Date(new Date().getFullYear(), 6, 1);
  } else if (dateRange === "Q4") {
    start = new Date(new Date().getFullYear(), 9, 1);
  } else {
    start = new Date(end.getFullYear(), end.getMonth(), 1);
  }
  start.setHours(0, 0, 0, 0);
  return toISODate(start);
}

export function getDateRangeBounds(dateRange, appliedCustomStart, appliedCustomEnd) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let start, end;
  if (dateRange === "Custom Range" && appliedCustomStart && appliedCustomEnd) {
    start = typeof appliedCustomStart === "string" ? parseISODate(appliedCustomStart) : new Date(appliedCustomStart.getFullYear(), appliedCustomStart.getMonth(), appliedCustomStart.getDate());
    end = typeof appliedCustomEnd === "string" ? parseISODate(appliedCustomEnd) : new Date(appliedCustomEnd.getFullYear(), appliedCustomEnd.getMonth(), appliedCustomEnd.getDate());
  } else if (dateRange === "This Week") {
    const day = today.getDay();
    start = new Date(today);
    start.setDate(today.getDate() - day);
    end = new Date(today);
  } else if (dateRange === "MTD") {
    start = new Date(today.getFullYear(), today.getMonth(), 1);
    end = new Date(today);
  } else if (dateRange === "Last Week") {
    const day = today.getDay();
    start = new Date(today);
    start.setDate(today.getDate() - day - 7);
    end = new Date(today);
    end.setDate(today.getDate() - day - 1);
  } else if (dateRange === "Last Month") {
    start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    end = new Date(today.getFullYear(), today.getMonth(), 0);
  } else if (dateRange === "Last Quarter") {
    const prevQ = (Math.floor(today.getMonth() / 3) + 3) % 4;
    const year = today.getMonth() < 3 ? today.getFullYear() - 1 : today.getFullYear();
    start = new Date(year, prevQ * 3, 1);
    end = new Date(year, prevQ * 3 + 3, 0);
  } else if (dateRange === "Q1") {
    start = new Date(today.getFullYear(), 0, 1);
    end = new Date(today.getFullYear(), 2, 31);
  } else if (dateRange === "Q2") {
    start = new Date(today.getFullYear(), 3, 1);
    end = new Date(today.getFullYear(), 5, 30);
  } else if (dateRange === "Q3") {
    start = new Date(today.getFullYear(), 6, 1);
    end = new Date(today.getFullYear(), 8, 30);
  } else if (dateRange === "Q4") {
    start = new Date(today.getFullYear(), 9, 1);
    end = new Date(today.getFullYear(), 11, 31);
  } else {
    start = new Date(today.getFullYear(), today.getMonth(), 1);
    end = new Date(today);
  }
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return { startDate: toISODate(start), endDate: toISODate(end) };
}

// Convert YYYY-MM-DD bounds to UTC ISO strings treating the date strings as UTC calendar days.
// Constructing directly from the string avoids local-timezone offsets shifting the boundary
// (e.g. a US Pacific user's local midnight would become T07:00:00Z, silently excluding UTC-midnight rows).
// endISO uses microsecond precision (.999999Z) — the last representable instant in a Postgres
// timestamptz — so inclusive <= filters don't silently drop sub-millisecond end-of-day records.
export function toUTCStartEnd(startDate, endDate) {
  return {
    startISO: `${startDate}T00:00:00.000000Z`,
    endISO: `${endDate}T23:59:59.999999Z`,
  };
}

// Number of UTC days from startDate to endDate ("YYYY-MM-DD"), both included.
export function daysInRange(startDate, endDate) {
  return (Date.parse(`${endDate}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`)) / 86400000 + 1;
}

export function formatConversionDate(isoStr) {
  if (!isoStr) return { date: "—", time: "—" };
  const d = new Date(isoStr);
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "UTC" });
  return { date, time };
}

export function parseTimelineDate(ts) {
  if (!ts) return null;
  const d = typeof ts === "string" && /^\d{4}-\d{2}-\d{2}/.test(ts) ? new Date(ts) : new Date(ts);
  return isNaN(d.getTime()) ? null : d;
}

export function formatTimelineMeta(d) {
  if (!d) return "—";
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "UTC" });
  return `${date} \u00B7 ${time}`;
}
